// Eine Karte in der Board-Liste. Rename via inline-Edit, Archive + Open Buttons.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BoardDto } from "./api.js";
import { normalizeRename } from "./rename.js";

interface BoardCardProps {
  board: BoardDto;
  isOwner: boolean;
  onOpen: (id: string) => void;
  onRename: (id: string, name: string) => Promise<unknown>;
  onArchive: (id: string) => Promise<unknown>;
}

export function BoardCard({
  board,
  isOwner,
  onOpen,
  onRename,
  onArchive,
}: BoardCardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(board.name);
  const [busy, setBusy] = useState(false);

  const commitRename = async (): Promise<void> => {
    const next = normalizeRename(name, board.name);
    if (next === null) {
      setEditing(false);
      setName(board.name);
      return;
    }
    setBusy(true);
    try {
      await onRename(board.id, next);
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  const archive = async (): Promise<void> => {
    setBusy(true);
    try {
      await onArchive(board.id);
    } finally {
      setBusy(false);
    }
  };

  const updated = new Date(board.updatedAt).toLocaleString(i18n.language);

  return (
    <article className={`board-card board-card-${board.status}`} data-board-id={board.id}>
      <header className="board-card-header">
        {editing ? (
          <input
            className="board-card-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") void commitRename();
              if (e.key === "Escape") {
                setEditing(false);
                setName(board.name);
              }
            }}
            // biome-ignore lint/a11y/noAutofocus: rename-input öffnet on demand, focus erwünscht
            autoFocus
          />
        ) : (
          <h3 className="board-card-name" title={board.name}>
            {board.name}
          </h3>
        )}
        {board.status === "archived" && (
          <span className="board-card-badge">{t("starter.badge.archived")}</span>
        )}
      </header>
      <p className="board-card-meta">
        <span>{t("starter.card.updated", { ts: updated })}</span>
      </p>
      <footer className="board-card-actions">
        <button
          type="button"
          className="primary starter-card-open"
          onClick={() => onOpen(board.id)}
          disabled={busy}
        >
          {t("starter.card.open")}
        </button>
        {isOwner && board.status === "active" && (
          <>
            <button type="button" onClick={() => setEditing(true)} disabled={busy || editing}>
              {t("starter.card.rename")}
            </button>
            <button type="button" onClick={archive} disabled={busy}>
              {t("starter.card.archive")}
            </button>
          </>
        )}
      </footer>
    </article>
  );
}
